import React, {
  useState,
  useMemo,
  useEffect,
  useRef,
  useCallback,
} from "react";
import Model, { helper, Providers } from "@tripian/model";
import { CustomSlider, PreLoading, SvgIcons, TourCard } from "@tripian/react";
import moment from "moment";
import { useDebounce } from "../../../../../../hooks/useDebounce";
import { useBookableCatalogs } from "../../../../../../hooks/useBookableCatalogs";
import useGygCatalog from "../../../../../../hooks/useGygCatalog";
import useViatorCatalog from "../../../../../../hooks/useViatorCatalog";
import useToristyCatalog from "../../../../../../hooks/useToristyCatalog";
import useBbTours from "../../../../../../hooks/useBbTours";
import useRezdyApi from "../../../../../../hooks/useRezdyApi";
import useVictoryEvents from "../../../../../../hooks/useVictoryEvents";
import useOccasionGeniusCatalog from "../../../../../../hooks/useOccasionGeniusCatalog";
import useLivnCatalog from "../../../../../../hooks/useLivnCatalog";
import usePriorityTours from "../../../../../../hooks/usePriorityTours";
import { displayLivnPrice } from "../../../../../../utils/livnPricing";
import classes from "./BookableExperiencesContainer.module.scss";

type BaseBookableItem =
    | Providers.Gyg.Tour
    | Providers.Bb.Product
    | Providers.Viator.Product
    | Providers.Rezdy.Product
    | Providers.Toristy.Product
    | Providers.Victory.Event
    | Providers.OccasionGenius.Event
    | Providers.Livn.Product;

type BookableItem = BaseBookableItem & { provider: Model.PROVIDER_ID };

interface Category {
  title: string;
  items: BookableItem[];
}

const CATEGORIES: { id: string; label: string; title: string }[] = [
  { id: "all", title: "All", label: "trips.categories.all" },
  {
    id: "adventure",
    title: "Adventure",
    label: "trips.myTrips.localExperiences.categories.adventure",
  },
  {
    id: "food",
    title: "Food",
    label: "trips.myTrips.localExperiences.categories.food",
  },
  {
    id: "culture",
    title: "Culture History",
    label: "trips.myTrips.localExperiences.categories.cultureHistory",
  },
  {
    id: "sight",
    title: "Sightseeing",
    label: "trips.myTrips.localExperiences.categories.sightSeeing",
  },
  {
    id: "art",
    title: "Art Museum",
    label: "trips.myTrips.localExperiences.categories.artMuseum",
  },
  {
    id: "local",
    title: "Local Neighborhood",
    label: "trips.myTrips.localExperiences.categories.localNeighborhood",
  },
  {
    id: "events",
    title: "Events",
    label: "trips.myTrips.localExperiences.categories.events",
  },
  {
    id: "others",
    title: "Others",
    label: "trips.myTrips.localExperiences.categories.others",
  },
];

const PROVIDERS = [
  { id: Model.PROVIDER_ID.GYG, name: "GetYourGuide" },
  { id: Model.PROVIDER_ID.BOOK_BARBADOS, name: "BookBarbados" },
  { id: Model.PROVIDER_ID.VIATOR, name: "Viator" },
  { id: Model.PROVIDER_ID.REZDY, name: "Rezdy" },
  { id: Model.PROVIDER_ID.TORISTY, name: "Toristy" },
  { id: Model.PROVIDER_ID.VICTORY, name: "Victory Live" },
  { id: Model.PROVIDER_ID.OCCASION_GENIUS, name: "OccasionGenius" },
  { id: Model.PROVIDER_ID.LIVN, name: "Livn" },
];

interface IBookableExperiencesContainerUI {
  show: boolean;
  tripReference: Model.TripReference;
  finalCatalogs: Category[];
  priorityCatalog?: BookableItem[];
  t: (value: Model.TranslationKey) => string;
  onTourClick: (
    provider: Model.PROVIDER_ID,
    tourId: string,
    additionalData?: any
  ) => void;
  isLoading: boolean;
}

const BookableExperiencesContainerUI: React.FC<IBookableExperiencesContainerUI> = ({
  show,
  tripReference,
  finalCatalogs,
  priorityCatalog,
  t,
  onTourClick,
  isLoading,
}) => {
  const [searchText, setSearchText] = useState<string>("");
  const [isSearchLoading, setIsSearchLoading] = useState<boolean>(false);
  const [visibleItems, setVisibleItems] = useState<BookableItem[]>([]);
  const [page, setPage] = useState<number>(1);
  const [showFilters, setShowFilters] = useState<boolean>(false);

  const itemsPerPage = 10;
  const containerRef = useRef<HTMLDivElement>(null);
  const observer = useRef<IntersectionObserver | null>(null);

  const loadingRef = useCallback(
    (node: HTMLDivElement) => {
      if (observer.current) observer.current.disconnect();
      observer.current = new IntersectionObserver(entries => {
        if (entries[0].isIntersecting && !isSearchLoading) {
          setPage(prevPage => prevPage + 1);
        }
      });
      if (node) observer.current.observe(node);
    },
    [isSearchLoading]
  );

  const initialCategory = "all";
  const [selectedCategory, setSelectedCategory] =
    useState<string>(initialCategory);
  const availableProviders = React.useMemo(() => {
    const dynamicProviders = window.tconfig?.PROVIDERS?.tourAndTicket || [];
    if (!Array.isArray(dynamicProviders) || dynamicProviders.length === 0)
      return [];
    return PROVIDERS.filter(staticProv =>
      dynamicProviders.some((dynProv: any) => staticProv.id === dynProv.id)
    );
  }, []);

  const nonEmptyCatalogs = useMemo(() => {
    return finalCatalogs.filter(catalog => catalog.items.length > 0);
  }, [finalCatalogs]);

  const presentProviders = useMemo(() => {
    return availableProviders.filter(prov =>
      nonEmptyCatalogs.some(cat =>
        cat.items.some((item: any) => item.provider === prov.id)
      )
    );
  }, [availableProviders, nonEmptyCatalogs]);

  const [selectedProviders, setSelectedProviders] = useState<number[]>([]);
  React.useEffect(() => {
    setSelectedProviders(presentProviders.map(p => p.id));
  }, [presentProviders]);

  const handleCategoryChange = (categoryId: string) => {
    setSelectedCategory(categoryId);
    setSearchText("");
    setIsSearchLoading(false);
    setPage(1);
    setVisibleItems([]);
    if (containerRef.current) {
      containerRef.current.scrollTop = 0;
    }
  };

  const handleProviderChange = (providerId: number) => {
    if (selectedProviders.includes(providerId)) {
      const filtered = selectedProviders.filter(p => p !== providerId);
      setSelectedProviders(filtered);
    } else {
      setSelectedProviders([...selectedProviders, providerId]);
    }
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(e.target.value);
    setIsSearchLoading(true);
    setPage(1);
    setVisibleItems([]);
  };

  const containerClasses = [classes.bookableExperiencesContainer];
  if (!window.tconfig.SHOW_OVERVIEW)
    containerClasses.push("trip-layout-container-height");
  else containerClasses.push("trip-layout-container-height-tab");
  if (show) containerClasses.push(classes.containerOpen);
  else containerClasses.push(classes.containerClose);

  const selectedCategoryTitle = CATEGORIES.find(
    cat => cat.id === selectedCategory
  )?.title;

  const debouncedSearchText = useDebounce(searchText, 300);

  const getItemId = (item: BookableItem): string => {
    switch (item.provider) {
      case Model.PROVIDER_ID.GYG:
        return `gyg-${(item as Providers.Gyg.Tour).tour_id}`;
      case Model.PROVIDER_ID.VIATOR:
        return `viator-${(item as Providers.Viator.Product).productCode}`;
      case Model.PROVIDER_ID.REZDY:
        return `rezdy-${(item as Providers.Rezdy.Product).productCode}`;
      case Model.PROVIDER_ID.TORISTY:
        return `toristy-${(item as Providers.Toristy.Product).id}`;
      case Model.PROVIDER_ID.VICTORY:
        return `victory-${(item as Providers.Victory.Event).id}`;
      case Model.PROVIDER_ID.BOOK_BARBADOS:
        return `bb-${(item as Providers.Bb.Product).info.id}`;
      case Model.PROVIDER_ID.OCCASION_GENIUS:
        return `og-${(item as Providers.OccasionGenius.Event).id}`;
      case Model.PROVIDER_ID.LIVN:
        return `livn-${(item as Providers.Livn.Product).id}`;
      default:
        return `unknown-${Math.random()}`;
    }
  };

  const getUniqueItems = (items: BookableItem[]): BookableItem[] => {
    const seen = new Set<string>();
    return items.filter(item => {
      const uniqueId = getItemId(item);
      if (seen.has(uniqueId)) return false;
      seen.add(uniqueId);
      return true;
    });
  };

  const filteredItems = useMemo(() => {
    let categoryItems: BookableItem[] = [];
    if (selectedCategory === "all") {
      categoryItems = nonEmptyCatalogs.reduce(
        (acc, cat) => [...acc, ...cat.items],
        [] as BookableItem[]
      );
    } else {
      categoryItems =
        nonEmptyCatalogs.find(cat => cat.title === selectedCategoryTitle)
          ?.items || [];
    }
    categoryItems = getUniqueItems(categoryItems);
    categoryItems = categoryItems.filter((item: any) =>
      selectedProviders.includes(item.provider)
    );

    if (priorityCatalog && priorityCatalog.length > 0 && selectedCategory === "all") {
      const filteredPriorityItems = priorityCatalog.filter((item: any) =>
        selectedProviders.includes(item.provider)
      );
      const priorityIds = new Set(filteredPriorityItems.map(item => getItemId(item)));
      const nonPriorityItems = categoryItems.filter(item => !priorityIds.has(getItemId(item)));
      categoryItems = [...filteredPriorityItems, ...nonPriorityItems];
    }

    if (!debouncedSearchText.trim() || debouncedSearchText.trim().length < 2) {
      setIsSearchLoading(false);
      return categoryItems;
    }
    setIsSearchLoading(true);
    setTimeout(() => {
      setIsSearchLoading(false);
    }, 300);

    return categoryItems.filter(item => {
      const provider = (item as any).provider;
      const searchLower = debouncedSearchText.toLowerCase();

      if (provider === Model.PROVIDER_ID.VICTORY) {
        const victoryEvent = item as Providers.Victory.Event;
        return victoryEvent.name.toLowerCase().includes(searchLower);
      }
      if (provider === Model.PROVIDER_ID.GYG) {
        const gygTour = item as Providers.Gyg.Tour;
        return gygTour.title.toLowerCase().includes(searchLower);
      }
      if (provider === Model.PROVIDER_ID.BOOK_BARBADOS) {
        const bbProduct = item as Providers.Bb.Product;
        return bbProduct.info.name.toLowerCase().includes(searchLower);
      }
      if (provider === Model.PROVIDER_ID.VIATOR) {
        const viatorProduct = item as Providers.Viator.Product;
        return viatorProduct.title.toLowerCase().includes(searchLower);
      }
      if (provider === Model.PROVIDER_ID.REZDY) {
        const rezdyProduct = item as Providers.Rezdy.Product;
        return (rezdyProduct.name || "").toLowerCase().includes(searchLower);
      }
      if (provider === Model.PROVIDER_ID.TORISTY) {
        const toristyProduct = item as Providers.Toristy.Product;
        return toristyProduct.name.toLowerCase().includes(searchLower);
      }
      if (provider === Model.PROVIDER_ID.OCCASION_GENIUS) {
        const ogEvent = item as Providers.OccasionGenius.Event;
        return ogEvent.event_name?.toLowerCase().includes(searchLower);
      }
      return false;
    });
  }, [
    selectedCategory,
    debouncedSearchText,
    nonEmptyCatalogs,
    selectedCategoryTitle,
    selectedProviders,
    priorityCatalog,
  ]);

  useEffect(() => {
    let isMounted = true;
    const start = 0;
    const end = page * itemsPerPage;
    if (isMounted) {
      setVisibleItems(filteredItems.slice(start, end));
    }
    return () => {
      isMounted = false;
    };
  }, [filteredItems, page]);

  const [showNoTours, setShowNoTours] = useState(false);

  useEffect(() => {
    let timeout: NodeJS.Timeout | undefined;

    if (isLoading) {
      setShowNoTours(false);
      return;
    }

    if (!isLoading && nonEmptyCatalogs.length === 0) {
      timeout = setTimeout(() => setShowNoTours(true), 1000);
    } else {
      setShowNoTours(false);
    }

    return () => {
      if (timeout) clearTimeout(timeout);
    };
  }, [isLoading, nonEmptyCatalogs.length]);

  const filterButtonRef = React.useRef<HTMLButtonElement>(null);
  const [popupPosition, setPopupPosition] = useState<{
    top: number;
    right: number;
    placement: "top" | "bottom";
  }>({
    top: 0,
    right: 0,
    placement: "bottom",
  });

  useEffect(() => {
    if (showFilters && filterButtonRef.current) {
      const rect = filterButtonRef.current.getBoundingClientRect();
      const windowHeight = window.innerHeight;

      const estimatedPopupHeight = 400;
      const safetyMargin = 20;

      const spaceBelow = windowHeight - rect.bottom;
      const spaceAbove = rect.top;

      if (spaceBelow >= estimatedPopupHeight + safetyMargin) {
        setPopupPosition({
          top: rect.bottom + 8,
          right: window.innerWidth - rect.right,
          placement: "bottom",
        });
      } else if (spaceAbove >= estimatedPopupHeight + safetyMargin) {
        setPopupPosition({
          top: Math.max(100, rect.top - estimatedPopupHeight + 100),
          right: window.innerWidth - rect.right,
          placement: "top",
        });
      } else {
        if (spaceBelow >= spaceAbove) {
          setPopupPosition({
            top: rect.bottom + 8,
            right: window.innerWidth - rect.right,
            placement: "bottom",
          });
        } else {
          setPopupPosition({
            top: Math.max(100, rect.top - estimatedPopupHeight + 100),
            right: window.innerWidth - rect.right,
            placement: "top",
          });
        }
      }
    }
  }, [showFilters]);

  const renderCard = (item: BookableItem) => {
    switch (item.provider) {
      case Model.PROVIDER_ID.VIATOR: {
        const viatorProduct = item as Providers.Viator.Product;
        const tags = (viatorProduct.flags || []).map(pr =>
          pr
            .toLowerCase()
            .split("_")
            .map(p => p && p[0].toUpperCase() + p.slice(1))
            .join(" ")
        );
        const images = (viatorProduct.images || []).map(img => {
          const maxVariant = img.variants?.reduce(
            (max, curr) => (curr.width > max.width ? curr : max),
            img.variants[0]
          );
          return maxVariant?.url || "";
        }).filter(url => url);
        return (
          <TourCard
            key={`viator-${viatorProduct.productCode}`}
            title={viatorProduct.title}
            price={viatorProduct.pricing.summary.fromPrice}
            currency={viatorProduct.pricing.currency}
            rating={viatorProduct.reviews?.combinedAverageRating}
            reviewCount={viatorProduct.reviews?.totalReviews || 0}
            tags={tags}
            images={images}
            provider="Viator"
            onBook={() => {
              const startDate = moment(
                tripReference?.tripProfile.arrivalDatetime
              ).format("YYYY-MM-DD");
              const endDate = moment(
                tripReference?.tripProfile.departureDatetime
              ).format("YYYY-MM-DD");
              onTourClick(Model.PROVIDER_ID.VIATOR, viatorProduct.productCode, {
                cityId: tripReference.city.id,
                cityName: tripReference.city.name,
                startDate,
                endDate,
                adults: tripReference.tripProfile.numberOfAdults,
                children: tripReference.tripProfile.numberOfChildren,
                url: viatorProduct.productUrl,
              });
            }}
            t={t}
          />
        );
      }

      case Model.PROVIDER_ID.REZDY: {
        const rezdyProduct = item as Providers.Rezdy.Product;
        return (
          <TourCard
            key={`rezdy-${rezdyProduct.productCode}`}
            title={rezdyProduct.name || ""}
            price={
              rezdyProduct.advertisedPrice ||
              rezdyProduct.priceOptions?.[0]?.price ||
              0
            }
            currency={rezdyProduct.currency}
            tags={rezdyProduct.tags}
            images={rezdyProduct.images.map(img => img.itemUrl)}
            provider="Rezdy"
            onBook={() => {
              const bookingWidgetUrl = `https://365adventures.rezdy.com/calendarWidget/${rezdyProduct.productCode}`;
              onTourClick(Model.PROVIDER_ID.REZDY, rezdyProduct.productCode, {
                url: bookingWidgetUrl,
              });
            }}
            t={t}
          />
        );
      }

      case Model.PROVIDER_ID.TORISTY: {
        const toristyProduct = item as Providers.Toristy.Product;
        return (
          <TourCard
            key={`toristy-${toristyProduct.id}`}
            title={toristyProduct.name}
            price={Number(toristyProduct.starting_price.price)}
            currency={toristyProduct.starting_price.currency}
            tags={toristyProduct.categories.map(
              (pr: { categoryname: string }) => pr.categoryname
            )}
            images={[toristyProduct.image.orig]}
            provider="Toristy"
            onBook={() => {
              onTourClick(Model.PROVIDER_ID.TORISTY, toristyProduct.id, {
                cityId: tripReference.city.id,
                cityName: tripReference.city.name,
                url:
                  toristyProduct.onlybookingwithreviewslink ||
                  toristyProduct.onlybookinglink,
              });
            }}
            t={t}
          />
        );
      }

      case Model.PROVIDER_ID.VICTORY: {
        const victoryEvent = item as Providers.Victory.Event;
        const tags: string[] = [];
        if (victoryEvent.category?.name) {
          tags.push(victoryEvent.category.name);
        }
        if (victoryEvent.category?.parent?.name) {
          tags.push(victoryEvent.category.parent.name);
        }
        return (
          <TourCard
            key={`victory-${victoryEvent.id}`}
            title={victoryEvent.name}
            tags={tags}
            images={[]}
            provider="Victory"
            t={t}
            onBook={() =>
              onTourClick(Model.PROVIDER_ID.VICTORY, victoryEvent.id.toString(), {
                url: victoryEvent.url,
              })
            }
          />
        );
      }

      case Model.PROVIDER_ID.GYG: {
        const gygTour = item as Providers.Gyg.Tour;
        const tourImages = gygTour.pictures
          .filter(picture => picture?.url)
          .map(picture => helper.getYourGuideImageFormat(picture.url, "21"));
        return (
          <TourCard
            key={`gyg-${gygTour.tour_id}`}
            title={gygTour.title}
            price={gygTour.price?.values.amount || 0}
            rating={gygTour.overall_rating}
            reviewCount={gygTour.number_of_ratings}
            tags={gygTour.categories
              .filter(category => !category.parent_id)
              .map(c => c.name)}
            images={tourImages}
            provider="GetYourGuide"
            onBook={() => {
              onTourClick(Model.PROVIDER_ID.GYG, gygTour.tour_id.toString(), {
                url: gygTour.url,
              });
            }}
            t={t}
          />
        );
      }

      case Model.PROVIDER_ID.OCCASION_GENIUS: {
        const ogEvent = item as Providers.OccasionGenius.Event;
        const ogTags: string[] = [];
        if (ogEvent.event_type) ogTags.push(ogEvent.event_type);
        if (ogEvent.primary_descriptor_interests) {
          ogEvent.primary_descriptor_interests
            .split(",")
            .map(s => s.trim().replace(/_/g, " "))
            .filter(Boolean)
            .forEach(interest => ogTags.push(interest));
        }
        return (
          <TourCard
            key={`og-${ogEvent.id}`}
            title={ogEvent.event_name}
            tags={ogTags}
            images={ogEvent.image_url ? [ogEvent.image_url] : []}
            provider="OccasionGenius"
            t={t}
            onBook={() => {
              onTourClick(Model.PROVIDER_ID.OCCASION_GENIUS, ogEvent.id, {
                url: ogEvent.source_url,
              });
            }}
          />
        );
      }

      case Model.PROVIDER_ID.LIVN: {
        const livnProduct = item as Providers.Livn.Product;
        // Net-rate products must carry the same markup here as on the detail/checkout screen,
        // otherwise the card advertises a price the customer can never actually pay.
        const livnFrom = livnProduct.fromPrices?.[0];
        const livnPrice = livnFrom ? displayLivnPrice(livnFrom, livnProduct) : undefined;
        return (
          <TourCard
            key={`livn-${livnProduct.id}`}
            title={livnProduct.name}
            price={livnPrice?.amount}
            currency={livnPrice?.currency}
            tags={livnProduct.categories?.map(c => c.name) || []}
            images={livnProduct.images?.map(img => img.url) || []}
            provider="Livn"
            onBook={() => {
              onTourClick(Model.PROVIDER_ID.LIVN, livnProduct.id.toString());
            }}
            t={t}
          />
        );
      }

      case Model.PROVIDER_ID.BOOK_BARBADOS: {
        const bbProduct = item as Providers.Bb.Product;
        const price =
          bbProduct.offers[0]?.priceBreakDowns.find(
            pb => pb.touristType === "ADULT"
          )?.price.amount || 0;
        const currency =
          bbProduct.offers[0]?.priceBreakDowns.find(
            pb => pb.touristType === "ADULT"
          )?.price.currency || "$";
        return (
          <TourCard
            key={`bb-${bbProduct.info.id}`}
            title={bbProduct.info.name}
            price={price}
            currency={currency}
            tags={bbProduct.themes}
            images={[bbProduct.mainImage]}
            provider="BookBarbados"
            onBook={() => {
              const bbProductOffer = bbProduct.offers[0];
              const activityBbUrl = `https://bookbarbados.com/book-experiences/experience-details/?packageoptions=experience&actid=${bbProduct.info.id}&offer=${
                bbProductOffer.offerKey
              }&check-in=${moment(bbProductOffer.date).format("MMM DD, YYYY")}&check-out=${moment(bbProductOffer.date).format("MMM DD, YYYY")}&adults=${
                tripReference.tripProfile.numberOfAdults
              }&children=${tripReference.tripProfile.numberOfChildren || ""}&prefillDate=${moment(bbProductOffer.date).format("MMM DD, YYYY")}&prefilladults=${
                tripReference.tripProfile.numberOfAdults
              }&prefillchildage=${tripReference.tripProfile.numberOfChildren || ""}&site=TRIPIAN`;

              onTourClick(
                Model.PROVIDER_ID.BOOK_BARBADOS,
                bbProduct.info.id.toString(),
                {
                  url: activityBbUrl,
                }
              );
            }}
            t={t}
          />
        );
      }

      default:
        return null;
    }
  };

  const renderContent = () => {
    if (isLoading) {
      return (
        <div className={classes.loadingContainer}>
          <div className="flex flex-col items-center justify-center">
            <PreLoading />
          </div>
        </div>
      );
    }

    if (showNoTours) {
      return (
        <div className="flex flex-col h-full items-center justify-center mt-10 text-gray-500">
          {t("trips.myTrips.localExperiences.toursEmpty")}
        </div>
      );
    }

    if (nonEmptyCatalogs.length === 0 && (!priorityCatalog || priorityCatalog.length === 0)) {
      return null;
    }

    return (
      <>
        <div className="whitespace-nowrap py-4 border-b">
          <CustomSlider scrollSize={150}>
            {CATEGORIES.map((cat, index) => {
              const hasItems =
                cat.id === "all"
                  ? true
                  : nonEmptyCatalogs.some(c => c.title === cat.title);
              if (!hasItems) return null;
              return (
                <div
                  key={index}
                  className={`text-sm !w-auto rounded-3xl border border-solid border-gray-300 my-0 py-[0.4375rem] px-4 cursor-pointer min-w-fit font-medium whitespace-nowrap ${
                    selectedCategory === cat.id
                      ? "text-white bg-primary-color"
                      : "text-black bg-white"
                  }`}
                  onClick={() => handleCategoryChange(cat.id)}
                >
                  <span className="font-medium">
                    {t(cat.label as Model.TranslationKey)}
                  </span>
                </div>
              );
            })}
          </CustomSlider>
        </div>
        <div className="relative whitespace-nowrap py-4 border-b">
          <div className="flex items-center w-full">
            <div className="flex-1 relative w-full">
              <input
                type="text"
                placeholder={t("trips.myTrips.exploreMore.placeholder")}
                className="w-full ps-10 pe-4 py-3 border-2 border-gray-300 rounded-full bg-white text-gray-800 placeholder-gray-500 focus:outline-none focus:border-primary-color transition-colors"
                value={searchText}
                onChange={handleSearchChange}
              />
              <SvgIcons.Search className="absolute start-4 top-1/2 transform -translate-y-1/2 text-gray-500" />
              <button
                className="absolute end-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                onClick={() => {
                  setSearchText("");
                  setIsSearchLoading(false);
                }}
              >
                {searchText && (
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"
                      fill="currentColor"
                    />
                  </svg>
                )}
              </button>
            </div>
            <div className="relative ms-2">
              <button
                className="py-3 px-4 rounded-full bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors flex items-center"
                onClick={() => setShowFilters(!showFilters)}
                aria-haspopup="true"
                aria-expanded={showFilters}
                ref={filterButtonRef}
              >
                <SvgIcons.Filter2 className="me-1" />
                {t("trips.filters.title")}
              </button>

              {showFilters && (
                <>
                  <div
                    className="fixed inset-0 z-40"
                    onClick={() => setShowFilters(false)}
                    style={{ pointerEvents: "all" }}
                  ></div>
                  <div
                    className="fixed w-80 bg-white rounded-lg shadow-xl z-50 border border-gray-200"
                    style={{
                      ...(popupPosition.placement === "bottom"
                        ? { top: `${popupPosition.top}px` }
                        : { top: `${popupPosition.top}px` }),
                      right: `${popupPosition.right}px`,
                      maxHeight: "70vh",
                    }}
                    onClick={e => e.stopPropagation()}
                  >
                    <div className="p-4 border-b flex justify-between items-center bg-white z-10 rounded-t-lg">
                      <h3 className="font-semibold text-gray-800">
                        {t("trips.filters.title")}
                      </h3>
                      <div className="flex items-center">
                        <button
                          className="text-sm bg-gray-100 hover:bg-gray-200 rounded-full px-3 py-1.5 text-gray-700 hover:text-gray-900 transition-colors me-3"
                          onClick={() => {
                            setSelectedProviders([]);
                            setShowFilters(false);
                          }}
                        >
                          {t("trips.filters.clearAll")}
                        </button>
                        <button
                          onClick={() => setShowFilters(false)}
                          className="text-gray-400 hover:text-gray-700 transition-colors w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100"
                        >
                          <SvgIcons.SearchClose
                            width="18"
                            height="18"
                            className="text-gray-400 hover:text-gray-700 transition-colors flex items-center justify-center rounded-full hover:bg-gray-100"
                          />
                        </button>
                      </div>
                    </div>
                    <div className="max-h-[400px] overflow-y-auto custom-scrollbar">
                      <div className="p-4">
                        <div className="mb-3">
                          <h4 className="font-medium mb-3 text-gray-700 border-s-4 border-primary-color ps-2">
                            {t("trips.filters.providers")}
                          </h4>
                          <div className="grid grid-cols-1 gap-2 ps-2">
                            {presentProviders.map(provider => (
                              <div
                                className="flex items-center hover:bg-gray-50 rounded-md p-1.5 -ms-1.5"
                                key={`filter-provider-${provider.name}`}
                              >
                                <div className="relative w-5 h-5">
                                  <input
                                    type="checkbox"
                                    id={`provider-${provider.name}`}
                                    checked={selectedProviders.includes(
                                      provider.id
                                    )}
                                    onChange={() =>
                                      handleProviderChange(provider.id)
                                    }
                                    className="appearance-none h-5 w-5 rounded-md bg-white border border-gray-300 checked:border-primary-color checked:bg-primary-color checked:border-0 focus:outline-none focus:ring-0 transition-colors"
                                  />
                                  <SvgIcons.Check2
                                    className={`absolute start-0.5 top-0.5 w-4 h-4 text-white pointer-events-none ${
                                      selectedProviders.includes(provider.id)
                                        ? "opacity-100"
                                        : "opacity-0"
                                    }`}
                                    fill="white"
                                  />
                                </div>
                                <label
                                  htmlFor={`provider-${provider.name}`}
                                  className="ms-2.5 text-gray-700 cursor-pointer text-sm"
                                >
                                  {provider.name}
                                </label>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        <div ref={containerRef} className="flex-1 overflow-auto py-4">
          {isSearchLoading ? (
            <div style={{ height: "4rem" }}>
              <PreLoading size="small" />
            </div>
          ) : filteredItems.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-8 text-gray-500">
              <span className="text-center text-text-primary-color text-sm min-h-16">
                {t("trips.myTrips.exploreMore.noResultsFound")}{" "}
              </span>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-6">
              {visibleItems.map(item => renderCard(item))}
              {!isSearchLoading &&
                visibleItems.length < filteredItems.length && (
                  <div
                    ref={loadingRef}
                    className="h-10 flex items-center justify-center"
                  >
                    <PreLoading size="small" />
                  </div>
                )}
            </div>
          )}
        </div>
      </>
    );
  };

  if (!show) {
    return null;
  }

  return (
    <div className={containerClasses.join(" ")}>
      <div className="flex flex-col h-full mb-16 md:mb-0">
        {renderContent()}
      </div>
    </div>
  );
};

interface IBookableExperiencesContainerLegacy {
  show: boolean;
  tripReference: Model.TripReference;
  t: (value: Model.TranslationKey) => string;
  onTourClick: (
    provider: Model.PROVIDER_ID,
    tourId: string,
    additionalData?: any
  ) => void;
}

const BookableExperiencesContainerLegacy: React.FC<IBookableExperiencesContainerLegacy> = ({
  show,
  tripReference,
  t,
  onTourClick,
}) => {
  const { gygToursCatalog, gygLoaders } = useGygCatalog(
    tripReference.city.name,
    tripReference.tripProfile.arrivalDatetime,
    tripReference.tripProfile.departureDatetime,
    tripReference.tripProfile.numberOfAdults,
    tripReference.tripProfile.numberOfChildren
  );

  const { viatorToursCatalog, loadingViatorTourCatalog } = useViatorCatalog(
    tripReference.city.name,
    tripReference.tripProfile.arrivalDatetime,
    tripReference.tripProfile.departureDatetime
  );

  const { toristyToursCatalog, loadingToristyTourCatalog } = useToristyCatalog(
    tripReference.city.id
  );

  const { bbToursCatalog, loadingBbTourCatalog } = useBbTours(
    tripReference.city.id,
    tripReference.tripProfile.arrivalDatetime,
    tripReference.tripProfile.departureDatetime
  );

  const { loadingRezdyTours, rezdyProductCatalogGroup } = useRezdyApi(
    tripReference.city.id
  );

  const { victoryEventsCatalog, loadingVictoryEventCatalog } = useVictoryEvents(
    tripReference.city.coordinate.lat,
    tripReference.city.coordinate.lng,
    tripReference.tripProfile.arrivalDatetime,
    tripReference.tripProfile.departureDatetime
  );

  const { occasionGeniusCatalog, loadingOccasionGeniusCatalog } = useOccasionGeniusCatalog(
    tripReference.city.coordinate.lat,
    tripReference.city.coordinate.lng,
    tripReference.tripProfile.arrivalDatetime,
    tripReference.tripProfile.departureDatetime
  );

  const { livnToursCatalog, loadingLivnTourCatalog } = useLivnCatalog(
    tripReference.city.coordinate.lat,
    tripReference.city.coordinate.lng,
    tripReference.tripProfile.arrivalDatetime,
    tripReference.tripProfile.departureDatetime
  );

  const { priorityProducts, loadingPriorityTours } = usePriorityTours();

  const { finalCatalogs, priorityCatalog, isLoading } = useBookableCatalogs({
    gygToursCatalog,
    bbToursCatalog,
    viatorToursCatalog,
    toristyToursCatalog,
    rezdyProductCatalogGroup,
    victoryEventsCatalog,
    occasionGeniusCatalog,
    livnToursCatalog,
    priorityProducts,
    loadingBbTourCatalog,
    gygLoaders,
    loadingViatorTourCatalog,
    loadingToristyTourCatalog,
    loadingRezdyTours,
    loadingVictoryEventCatalog,
    loadingOccasionGeniusCatalog,
    loadingLivnTourCatalog,
    loadingPriorityTours,
  });

  return (
    <BookableExperiencesContainerUI
      show={show}
      tripReference={tripReference}
      finalCatalogs={finalCatalogs}
      priorityCatalog={priorityCatalog}
      t={t}
      onTourClick={onTourClick}
      isLoading={isLoading}
    />
  );
};

export default BookableExperiencesContainerLegacy;